import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faClock,
  faLock,
} from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedAssessmentSessionId } from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { GrowthWheelAssessmentSession } from '../../types/GrowthWheel'

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not completed'

const formatStatus = (status: GrowthWheelAssessmentSession['status']) =>
  status === 'completed' ? 'Completed' : 'In Progress'

export function SessionSelector() {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)
  const assessmentSessions = useSelector(
    (state: RootState) => state.growthWheel.assessmentSessions,
  )
  const assessmentSession = useSelector(
    (state: RootState) => state.growthWheel.assessmentSession,
  )
  const selectedAssessmentSessionId = useSelector(
    (state: RootState) => state.growthWheel.selectedAssessmentSessionId,
  )
  const activeSessionId = useSelector(
    (state: RootState) => state.growthWheel.activeAssessmentSessionId,
  )
  const sessions =
    assessmentSession &&
    !assessmentSessions.some(session => session.id === assessmentSession.id)
      ? [assessmentSession, ...assessmentSessions]
      : assessmentSessions
  const selectedSessionId =
    selectedAssessmentSessionId ??
    activeSessionId ??
    sessions[0]?.id ??
    assessmentSession?.id ??
    null
  const sortedSessions = [...sessions].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  )
  const selectedSession =
    sessions.find(session => session.id === selectedSessionId) ?? null

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [])

  if (!selectedSession) {
    return (
      <div className="gw-session-selector__empty">No assessment session</div>
    )
  }

  return (
    <div className="gw-session-selector" ref={selectorRef}>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="gw-session-selector__trigger"
      >
        <FontAwesomeIcon
          icon={selectedSession.status === 'completed' ? faLock : faClock}
          className={
            selectedSession.status === 'completed'
              ? 'gw-session-selector__icon--locked'
              : 'gw-session-selector__icon--active'
          }
        />
        <span className="gw-session-selector__trigger-name">
          {selectedSession.name}
        </span>
        <span
          className={`gw-session-selector__status gw-session-selector__status--${selectedSession.status}`}
        >
          {formatStatus(selectedSession.status)}
        </span>
        {selectedSession.growthWheelVersion ? (
          <span className="gw-session-selector__version">
            V{selectedSession.growthWheelVersion}
          </span>
        ) : null}
        <FontAwesomeIcon
          icon={faChevronDown}
          className="gw-session-selector__chevron"
        />
      </button>

      {isOpen && (
        <div className="gw-session-selector__panel">
          {sortedSessions.map(session => (
            <button
              key={session.id}
              type="button"
              onClick={() => {
                dispatch(setSelectedAssessmentSessionId(session.id))
                setIsOpen(false)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              className={`gw-session-selector__item ${
                session.id === selectedSessionId
                  ? 'gw-session-selector__item--selected'
                  : ''
              }`}
            >
              <div className="gw-session-selector__item-head">
                <span className="gw-session-selector__item-name">
                  {session.name}
                </span>
                <span
                  className={`gw-session-selector__status gw-session-selector__status--${session.status}`}
                >
                  {formatStatus(session.status)}
                </span>
              </div>
              <div className="gw-session-selector__meta">
                <span>Started {formatDate(session.createdAt)}</span>
                <span>
                  {formatDate(session.completedAt) === 'Not completed'
                    ? 'Not completed'
                    : `Completed ${formatDate(session.completedAt)}`}
                </span>
                <span>GW V{session.growthWheelVersion ?? '-'}</span>
                {session.id === activeSessionId ? (
                  <span className="gw-session-selector__active-label">
                    Active
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
